import type { Thunk, Action } from 'easy-peasy';
import { action, thunk } from 'easy-peasy';
import type { Injections } from '../../services/injections';
import type { ApplicationErrorOptions } from '../../../types';
import parseStatusCode from '../../errors/parseStatusCode';
import type {
  LabelAugmentation,
  UpdateLabelAugmentation,
  CreateLabelAugmentation,
  FetchAssetParams,
  UpdateLabelAugmentationTagging,
  LabelAugmentationSet,
  CreateAugmentationSetWithLinks,
} from '../types';

export interface LabelAugmentationModel {
  labelAugmentations: Record<string, LabelAugmentation>;
  labelAugmentationFailure: Action<LabelAugmentationModel, ApplicationErrorOptions>;
  updateLabelAugmentation: Thunk<
    LabelAugmentationModel,
    UpdateLabelAugmentation,
    Injections,
    Promise<LabelAugmentation>
  >;
  createLabelAugmentation: Thunk<
    LabelAugmentationModel,
    CreateLabelAugmentation,
    Injections,
    Promise<LabelAugmentation>
  >;
  fetchLabelAugmentations: Thunk<LabelAugmentationModel, FetchAssetParams, Injections, Promise<LabelAugmentation[]>>;
  updateLabelAugmentationTagging: Thunk<
    LabelAugmentationModel,
    UpdateLabelAugmentationTagging,
    Injections,
    Promise<LabelAugmentation>
  >;
  deleteLabelAugmentationTagging: Thunk<
    LabelAugmentationModel,
    UpdateLabelAugmentationTagging,
    Injections,
    Promise<LabelAugmentation>
  >;
  setLabelAugmentations: Action<LabelAugmentationModel, LabelAugmentation[]>;
  lastAction?: number;
  labelAugmentationStart: Action<LabelAugmentationModel, void>;
  fetchLabelAugmentationSets: Thunk<
    LabelAugmentationModel,
    FetchAssetParams,
    Injections,
    Promise<LabelAugmentationSet[]>
  >;
  labelAugmentationSets: Record<string, LabelAugmentationSet>;
  setLabelAugmentationSets: Action<LabelAugmentationModel, LabelAugmentationSet[]>;
  createLabelAugmentationsSetWithLinks: Thunk<
    LabelAugmentationModel,
    CreateAugmentationSetWithLinks,
    Injections,
    Promise<LabelAugmentation>
  >;
}

export const labelAugmentationModel: LabelAugmentationModel = {
  labelAugmentations: {},
  labelAugmentationSets: {},
  labelAugmentationStart: action((state, payload) => {
    state.lastAction = Date.now();
  }),
  labelAugmentationFailure: action((state, payload) => {
    console.error(payload);
  }),
  fetchLabelAugmentations: thunk(async (actions, payload, { injections }) => {
    try {
      actions.labelAugmentationStart();
      const res = await injections.assetService.fetchLabelAugmentations(payload);
      actions.setLabelAugmentations(res);
      return res;
    } catch (error) {
      actions.labelAugmentationFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured creating label augmentation.',
        asNotification: true,
      });
    }
  }),
  createLabelAugmentationsSetWithLinks: thunk(async (actions, payload, { injections }) => {
    try {
      actions.labelAugmentationStart();
      const res = await injections.assetService.createLabelAugmentationsSetWithLinks(payload);
      actions.setLabelAugmentationSets([res]);
      return res;
    } catch (error) {
      actions.labelAugmentationFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured creating label augmentation set.',
        asNotification: true,
      });
    }
  }),
  fetchLabelAugmentationSets: thunk(async (actions, payload, { injections }) => {
    try {
      actions.labelAugmentationStart();
      const res = await injections.assetService.fetchLabelAugmentationSets(payload);
      actions.setLabelAugmentationSets(res);
      return res;
    } catch (error) {
      actions.labelAugmentationFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured creating label augmentation.',
        asNotification: true,
      });
    }
  }),
  createLabelAugmentation: thunk(async (actions, payload, { injections }) => {
    try {
      actions.labelAugmentationStart();
      const res = await injections.assetService.createLabelAugmentation(payload);
      actions.setLabelAugmentations([res]);
      return res;
    } catch (error) {
      actions.labelAugmentationFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured creating label augmentation.',
        asNotification: true,
      });
    }
  }),
  updateLabelAugmentation: thunk(async (actions, payload, { injections }) => {
    try {
      actions.labelAugmentationStart();
      const res = await injections.assetService.updateLabelAugmentation(payload);
      actions.setLabelAugmentations([res]);
      return res;
    } catch (error) {
      actions.labelAugmentationFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured updating label augmentation.',
        asNotification: true,
      });
    }
  }),
  updateLabelAugmentationTagging: thunk(async (actions, payload, { injections }) => {
    try {
      actions.labelAugmentationStart();
      const res = await injections.assetService.updateLabelAugmentationTagging(payload);
      actions.setLabelAugmentations([res]);
      return res;
    } catch (error) {
      actions.labelAugmentationFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured updating label augmentation.',
        asNotification: true,
      });
    }
  }),
  deleteLabelAugmentationTagging: thunk(async (actions, payload, { injections }) => {
    try {
      actions.labelAugmentationStart();
      const res = await injections.assetService.deleteLabelAugmentationTagging(payload);
      actions.setLabelAugmentations([res]);
      return res;
    } catch (error) {
      actions.labelAugmentationFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured updating label augmentation.',
        asNotification: true,
      });
    }
  }),
  setLabelAugmentations: action((state, payload) => {
    for (const val of payload) {
      state.labelAugmentations[val.labelAugmentationId] = val;
    }
  }),
  setLabelAugmentationSets: action((state, payload) => {
    for (const val of payload) {
      state.labelAugmentationSets[val.augmentationSetId] = val;
    }
  }),
};
